import React, { useCallback, useEffect, useState } from 'react'
import { ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { authFetch, getLocalApiUrl } from '../../../config/api'

function riskStyles(score) {
  if (score === 'High') return { bg: '#FCEBEB', fg: '#791F1F' }
  if (score === 'Medium') return { bg: '#FAEEDA', fg: '#633806' }
  return { bg: '#EAF3DE', fg: '#27500A' }
}

function formatWhen(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return String(value)
  }
}

/**
 * Collapsible ECDD panel listing saved patron screening scans (audit trail).
 * Loads GET /api/patron-screening/history?patronId=…
 */
export default function SearchAuditPanel({
  patronId,
  memberName = '',
  refreshKey = 0,
  onOpenScreening,
  canOpenScreening = false,
}) {
  const [collapsed, setCollapsed] = useState(true)
  const [scans, setScans] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [detail, setDetail] = useState(null)

  const loadHistory = useCallback(async () => {
    if (!patronId) {
      setScans([])
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams({
        patronId: String(patronId),
        limit: '20',
      })
      const res = await authFetch(getLocalApiUrl(`/api/patron-screening/history?${q}`))
      const json = await res.json().catch(() => ({}))
      if (!json?.success) throw new Error(json?.error || `HTTP ${res.status}`)
      setScans(Array.isArray(json.scans) ? json.scans : [])
    } catch (e) {
      setError(e?.message || 'Failed to load search audit')
      setScans([])
    } finally {
      setLoading(false)
    }
  }, [patronId])

  useEffect(() => {
    if (!collapsed) loadHistory()
  }, [collapsed, loadHistory, refreshKey])

  async function toggleDetail(scan) {
    if (expandedId === scan.id) {
      setExpandedId(null)
      setDetail(null)
      setDetailError(null)
      return
    }
    setExpandedId(scan.id)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/patron-screening/scans/${scan.id}`))
      const json = await res.json().catch(() => ({}))
      if (!json?.success) throw new Error(json?.error || `HTTP ${res.status}`)
      setDetail(json.scan || null)
    } catch (e) {
      setDetailError(e?.message || 'Failed to load scan details')
    } finally {
      setDetailLoading(false)
    }
  }

  const countLabel = loading ? '…' : String(scans.length)
  const hasScans = scans.length > 0

  return (
    <Card className="mb-3">
      <div
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-lg"
      >
        <div
          className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
          style={{
            backgroundColor: hasScans ? '#f0fdf4' : '#f8fafc',
            border: `2px solid ${hasScans ? '#16a34a' : '#e5e7eb'}`,
            color: hasScans ? '#16a34a' : '#6B7280',
          }}
        >
          <MagnifyingGlassIcon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-base font-semibold text-psg-teal">Search Audit</span>
            <span className={`text-sm font-mono ${hasScans ? 'text-green-600' : 'text-gray-500'}`}>
              {countLabel}
            </span>
          </div>
          <div className="text-sm text-gray-500 mt-0.5 truncate">
            Saved screening searches — subject criteria, kept hit titles/links, and outcome.
          </div>
        </div>
        <ChevronRightIcon
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${collapsed ? '' : 'rotate-90'}`}
        />
      </div>

      {!collapsed && (
        <CardContent className="px-4 pb-4 pt-3 border-t border-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="text-xs text-gray-500 m-0">
              {patronId
                ? `Member ${patronId}${memberName ? ` · ${memberName}` : ''}`
                : 'No member id on this ECDD — cannot filter audit history.'}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loading || !patronId}
                onClick={(e) => {
                  e.stopPropagation()
                  loadHistory()
                }}
              >
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
              {canOpenScreening && typeof onOpenScreening === 'function' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenScreening()
                  }}
                >
                  <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                  Run screening
                </Button>
              )}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-700 mb-2">Error: {error}</p>
          )}

          {!loading && !error && !patronId && (
            <p className="text-sm text-gray-500">Link a gaming member to this ECDD to see search history.</p>
          )}

          {!loading && !error && patronId && scans.length === 0 && (
            <p className="text-sm text-gray-500">
              No saved screening searches yet. Use <span className="font-medium text-psg-teal">Screening sources</span> and
              save a scan to the audit log.
            </p>
          )}

          {scans.length > 0 && (
            <div className="flex flex-col gap-2">
              {scans.map((scan) => {
                const when = formatWhen(scan.createdAt)
                const subj = [scan.subject?.firstName, scan.subject?.lastName].filter(Boolean).join(' ') || '—'
                const { bg: riskBg, fg: riskFg } = riskStyles(scan.riskScore)
                const isOpen = expandedId === scan.id
                return (
                  <div
                    key={scan.id}
                    className="rounded-lg border border-gray-200 bg-white overflow-hidden"
                  >
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2.5 flex items-start justify-between gap-3 hover:bg-gray-50/80 transition-colors"
                      onClick={() => toggleDetail(scan)}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{subj}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {when}
                          {scan.createdBy ? ` · by ${scan.createdBy}` : ''}
                          {scan.purpose ? ` · ${scan.purpose}` : ''}
                        </div>
                        {scan.note ? (
                          <div className="text-xs text-gray-600 mt-1 truncate">Note: {scan.note}</div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-1.5 items-center justify-end flex-shrink-0">
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-800">
                          {scan.sourcesHit ?? 0} hit
                        </span>
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-800">
                          {scan.sourcesClear ?? 0} clear
                        </span>
                        {(scan.sourcesError ?? 0) > 0 && (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-900">
                            {scan.sourcesError} err
                          </span>
                        )}
                        {scan.riskScore && (
                          <span
                            className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: riskBg, color: riskFg }}
                          >
                            {scan.riskScore}
                          </span>
                        )}
                        <ChevronRightIcon
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-100 bg-[#FAFAF8]">
                        {detailLoading && (
                          <p className="text-xs text-gray-500 m-0">Loading source results…</p>
                        )}
                        {detailError && (
                          <p className="text-xs text-red-700 m-0">{detailError}</p>
                        )}
                        {!detailLoading && detail && (
                          <div className="space-y-2">
                            <div className="text-[11px] text-gray-500 font-mono">
                              Scan {detail.scanUuid || detail.id}
                              {detail.sourcesTotal != null ? ` · ${detail.sourcesTotal} sources` : ''}
                            </div>
                            {detail.subject && (
                              <div className="text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md px-2.5 py-2">
                                <div className="font-medium text-gray-800 mb-1">Searched with</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {[
                                    ['Name', [detail.subject.firstName, detail.subject.lastName].filter(Boolean).join(' ')],
                                    ['Country', detail.subject.country],
                                    ['DOB', detail.subject.dob],
                                    ['Company', detail.subject.company],
                                    ['Aliases', detail.subject.aliases],
                                    ['ID', detail.subject.idNumber],
                                  ].filter(([, v]) => v).map(([label, value]) => (
                                    <span key={label} className="px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-100">
                                      <span className="text-slate-500">{label}:</span> {value}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {(detail.findings || []).length === 0 ? (
                              <p className="text-xs text-gray-500 m-0">No per-source findings stored.</p>
                            ) : (
                              <ul className="m-0 p-0 list-none space-y-1.5">
                                {detail.findings.map((f) => {
                                  const statusBg =
                                    f.status === 'hit' ? '#FCEBEB'
                                      : f.status === 'error' ? '#FAEEDA'
                                        : '#EAF3DE'
                                  const statusFg =
                                    f.status === 'hit' ? '#791F1F'
                                      : f.status === 'error' ? '#633806'
                                        : '#27500A'
                                  const kept = Array.isArray(f.payload?.keptResults)
                                    ? f.payload.keptResults
                                    : []
                                  return (
                                    <li
                                      key={f.id}
                                      className="text-xs rounded-md border border-gray-200 bg-white px-2.5 py-2"
                                    >
                                      <div className="flex gap-2 items-start">
                                        <span
                                          className="font-medium px-1.5 py-0.5 rounded flex-shrink-0 capitalize"
                                          style={{ backgroundColor: statusBg, color: statusFg }}
                                        >
                                          {f.status || '—'}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                          <div className="font-medium text-gray-800">{f.sourceName || f.sourceId}</div>
                                          {f.summary ? (
                                            <div className="text-gray-500 mt-0.5">{f.summary}</div>
                                          ) : null}
                                          {kept.length > 0 && (
                                            <ul className="mt-2 m-0 pl-0 list-none space-y-1.5">
                                              {kept.map((r, idx) => (
                                                <li key={`${f.id}-kept-${idx}`} className="rounded border border-gray-100 bg-[#FAFAF8] px-2 py-1.5">
                                                  {r.link ? (
                                                    <a
                                                      href={r.link}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="font-medium text-psg-teal hover:underline break-words"
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      {r.title || r.link}
                                                    </a>
                                                  ) : (
                                                    <div className="font-medium text-gray-800">{r.title || 'Result'}</div>
                                                  )}
                                                  {(r.source || r.date) && (
                                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                                      {[r.source, r.date].filter(Boolean).join(' · ')}
                                                    </div>
                                                  )}
                                                  {r.snippet ? (
                                                    <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{r.snippet}</div>
                                                  ) : null}
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                          {f.recordCount != null && kept.length === 0 && f.status === 'hit' && (
                                            <div className="text-[11px] text-amber-800 mt-1">
                                              {f.recordCount} record(s) counted — open Screening sources to re-run if links were not stored.
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </li>
                                  )
                                })}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
